import React, { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, Phone, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import portfolio from "@/data/portfolio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const c = portfolio.contact;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-40 border-t hairline bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-black/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
                09 · Contact
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-black leading-[1.0]">
              Let's build something exceptional.
            </h2>
            <p className="mt-6 text-lg text-gray-700 font-light leading-relaxed">
              Strategic mandates, board roles, partnerships, capital introductions — open inbox for serious operators.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={`mailto:${c.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 p-4 rounded-2xl bg-[#F5F5F7] border hairline hover:border-black/30 transition-colors group"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm text-gray-700 group-hover:text-black">{c.email}</span>
              </a>
              <a
                href={c.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-3 p-4 rounded-2xl bg-black text-white hover:bg-[#1a1a1a] transition-colors group"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Contact via WhatsApp</span>
              </a>
              {phoneRevealed ? (
                <a
                  href={`tel:${(c.phone || c.whatsapp || "").replace(/\s+/g, "")}`}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-3 p-4 rounded-2xl bg-[#F5F5F7] border hairline hover:border-black/30 transition-colors group"
                >
                  <Phone className="w-4 h-4" />
                  <span className="text-sm text-gray-700 group-hover:text-black">
                    {c.phone || c.whatsapp}
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setPhoneRevealed(true)}
                  data-testid="contact-phone-reveal"
                  className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#F5F5F7] border hairline hover:border-black/30 transition-colors group text-left"
                >
                  <Eye className="w-4 h-4" />
                  <span className="text-sm text-gray-700 group-hover:text-black">Show phone number</span>
                </button>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#F5F5F7] border hairline rounded-3xl p-6 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name">
                  <Input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Full name"
                    className="bg-white border-gray-200 rounded-xl h-12"
                    required
                  />
                </Field>
                <Field label="Email">
                  <Input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    className="bg-white border-gray-200 rounded-xl h-12"
                    required
                  />
                </Field>
                <Field label="Company">
                  <Input
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Organisation"
                    className="bg-white border-gray-200 rounded-xl h-12"
                  />
                </Field>
                <Field label="Subject">
                  <Input
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    placeholder="What is this about?"
                    className="bg-white border-gray-200 rounded-xl h-12"
                  />
                </Field>
              </div>
              <Field label="Message" className="mt-4">
                <Textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={6}
                  placeholder="Tell us about the opportunity, mandate or partnership."
                  className="bg-white border-gray-200 rounded-xl"
                  required
                />
              </Field>

              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-gray-500 max-w-sm">
                  Responses within 48 hours. For time-sensitive matters, WhatsApp is preferred.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 bg-black text-white hover:bg-[#1a1a1a] disabled:opacity-60 rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
                >
                  <Send className="w-4 h-4" />
                  {loading ? "Sending…" : "Send message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
